// prepareVideoPageMeta.js
import getMessageData from '../selector/getMessageData.js';
import getMessageDataWithVariable, {
  VariableName,
} from '../selector/getMessageDataWithVariable.js';
import getUserData from '../selector/getUserData.js';
import getMeData from '../selector/getMeData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { BRANDING_NAME } from '../RemoteConfigKeys.js';
import getMetaTagsForHelmet from '../resource/getMetaTagsForHelmet.js';
import { generateVideoObjectJsonLd } from '../resource/generateVideoObjectJsonLd.js';
import { getPoster } from '../resource/getMediaAsset.js';

/**
 * Prepare meta data for video pages
 * @param {Object} params
 * @param {Object} params.state - Redux state
 * @param {string} params.messageId - Message ID
 * @param {string} params.hostname - Request hostname
 * @param {string} params.canonical - Canonical URL
 * @param {Array} params.alternates - Alternate language links
 * @param {boolean} params.includeJsonLd - Whether to include JSON-LD
 * @param {boolean} params.forceNoindex - Force noindex (for draft pages)
 * @returns {Promise<Array>} Meta tags array for Helmet
 */
export const prepareVideoPageMeta = async ({
  state,
  messageId,
  hostname = 'swag.live',
  canonical = null,
  alternates = null,
  includeJsonLd = false,
  forceNoindex = false,
}) => {
  const brandingName = getRemoteConfigData(state, BRANDING_NAME) || 'SWAG';
  const meUsername = getMeData(state, 'username');
  const title =
    getMessageDataWithVariable(
      state,
      messageId,
      'originalTitle',
      `${VariableName.USERNAME}:${meUsername}`
    ).trim() || '';
  const caption =
    getMessageDataWithVariable(
      state,
      messageId,
      'originalCaptionText',
      `${VariableName.USERNAME}:${meUsername}`
    ).trim() || '';
  const description = caption || title || '';
  const durationSecs = getMessageData(state, messageId, 'media')?.duration || 0;
  const postedAtUnix = getMessageData(state, messageId, 'postedAtUnix');
  const unlockPrice = getMessageData(state, messageId, 'unlockPrice');
  const isFree = !unlockPrice;
  const hashtags = getMessageData(state, messageId, 'hashtags') || [];
  const categories = getMessageData(state, messageId, 'categories') || [];
  const sourceLanguage = getMessageData(state, messageId, 'sourceLanguage');
  const views = getMessageData(state, messageId, 'views') || 0;
  const likes = getMessageData(state, messageId, 'likes') || 0;
  const dislikes = getMessageData(state, messageId, 'dislikes') || 0;
  const unlocks = getMessageData(state, messageId, 'unlocks') || 0;

  // Get author data
  const senderId =
    getMessageData(state, messageId, 'sender') ||
    getMessageData(state, messageId, 'senderId');
  const username = senderId ? getUserData(state, senderId, 'username') : '';

  // Get thumbnail
  const thumbnailUrl = getPoster({
    id: messageId,
    size: '1024x1024',
    format: 'jpg',
    preset: 'sd',
  });

  // Prepare title and description
  const fullTitle = `${title} - ${username} - ${brandingName}`;
  const fullDescription = description;

  // Prepare keywords
  const keywords = [username, ...hashtags, ...categories, brandingName].filter(
    Boolean
  );

  // Prepare images
  const images = thumbnailUrl
    ? [{ url: thumbnailUrl, width: 1024, height: 1024 }]
    : [];

  // Prepare robots
  const robots = forceNoindex ? ['noindex', 'nofollow'] : [];

  // Prepare JSON-LD if requested
  let jsonLd = null;
  if (includeJsonLd && canonical && !forceNoindex) {
    const origin = `https://${hostname}`;
    const interactionStatistic = [
      { type: 'ViewAction', count: views },
      { type: 'BuyAction', count: unlocks },
      { type: 'LikeAction', count: likes },
      { type: 'DislikeAction', count: dislikes },
    ].filter(s => s.count);

    const assetIds = getMessageData(state, messageId, 'assetIds') || [];

    try {
      jsonLd = await generateVideoObjectJsonLd({
        id: messageId,
        title,
        description,
        durationSecs,
        senderId,
        username,
        postedAtUnix,
        isFree,
        unlockPrice,
        interactionStatistic,
        thumbnailUrl,
        hashtags,
        categories,
        origin,
        sourceLanguage,
        assetIds,
      });
    } catch (error) {
      jsonLd = null;
    }
  }

  // Prepare video actor metadata
  let videoActor = null;
  if (senderId && username) {
    videoActor = {
      id: senderId,
      username,
      url: `https://${hostname}/user/${senderId}/flix`,
    };

    const serialCode = title?.replace(/^[\s]*\[([^[\]]*)\].*/g, '$1');
    if (serialCode && serialCode !== title) {
      videoActor.tag = serialCode;
    }
  }

  return getMetaTagsForHelmet({
    title: fullTitle,
    description: fullDescription,
    keywords,
    hostname,
    ogType: 'video.other',
    url: canonical,
    siteName: brandingName,
    twitterCard: 'player',
    alternates: {
      canonical,
      alternates,
    },
    jsonLd,
    images,
    videoDuration: durationSecs,
    videoActor,
    robots,
  });
};

export default prepareVideoPageMeta;
